<template>
  <div class="attachment-viewer">
    <div class="viewer-header">
      <div class="header-left">
        <i class="el-icon-paperclip"></i>
        <span>共 {{ files.length }} 个附件</span>
      </div>
    </div>

    <!-- 暂无数据 -->
    <div class="empty-state"
         v-if="processedFiles.length < 1">
      <i class="el-icon-folder-opened"></i>
      <p>暂无附件</p>
    </div>
    <!-- 紧凑型文件网格 -->
    <div class="files-grid"
         v-else>
      <div v-for="(item, idx) in processedFiles"
           :key="idx"
           class="file-card"
           :title="item.tooltip">
        <!-- 图标区域 -->
        <div class="card-icon"
             :class="item.type"
             @click="previewFile(item)">
          <img v-if="item.isImg"
               :src="item.url"
               class="img-thumb"
               loading="lazy"
               @error="handleImgError"
               :alt="item.name"/>
          <i v-else
             :class="iconMap[item.type] || 'el-icon-files'"></i>
          <span class="file-badge">{{ labelMap[item.type] || '其他' }}</span>
        </div>

        <!-- 文件名 -->
        <div class="card-name"
             @click="previewFile(item)">{{ item.shortName }}
        </div>

        <!-- 上传人 -->
        <div class="card-uploader">
          <i class="el-icon-user"></i> {{ item.uploader }}
        </div>

        <!-- 显式操作按钮 -->
        <div class="card-actions-inline">
          <span class="action-text-btn"
                @click.stop="previewFile(item)">
            <i class="el-icon-view"></i> 查看
          </span>
          <span class="action-text-btn"
                @click.stop="downloadFile(item)">
            <i class="el-icon-download"></i> 下载
          </span>
        </div>
      </div>
    </div>

    <!-- 预览弹窗 -->
    <el-dialog :visible.sync="pvVisible"
               :title="pvTitle"
               width="90%"
               custom-class="preview-dialog"
               append-to-body
               @close="closePreview">
      <div class="preview-container"
           v-loading="pvLoading"
           element-loading-background="rgba(15, 23, 42, 0.8)">

        <!-- 1. 图片预览 -->
        <div v-if="pvType === 'image'"
             class="pv-image"
             @mousedown="dragStart"
             @wheel.prevent="onWheel">
          <img :src="pvUrl"
               :style="imgStyle"
               @load="pvLoading = false"
               @error="pvLoading = false"
               draggable="false"/>
          <div class="img-toolbar">
            <el-button-group>
              <el-button size="small"
                         icon="el-icon-zoom-in"
                         @click="zoomImg(0.2)"></el-button>
              <el-button size="small"
                         icon="el-icon-zoom-out"
                         @click="zoomImg(-0.2)"></el-button>
              <el-button size="small"
                         icon="el-icon-refresh-right"
                         @click="rotateImg"></el-button>
              <el-button size="small"
                         icon="el-icon-c-scale-to-original"
                         @click="resetImg"></el-button>
            </el-button-group>
            <span class="zoom-text">{{ Math.round(imgScale * 100) }}%</span>
          </div>
        </div>

        <!-- 2. PDF / HTML 预览 -->
        <iframe v-else-if="['pdf', 'html'].includes(pvType) && pvUrl"
                :src="pvUrl"
                class="pv-iframe"
                @load="pvLoading = false"></iframe>

        <!-- 3. EML 邮件预览 -->
        <div v-else-if="pvType === 'eml' && emlData"
             class="pv-eml">
          <div class="eml-header">
            <h2 class="eml-subject">{{ emlData.subject }}</h2>
            <div class="eml-meta">
              <div class="meta-item"><span class="meta-label">发件人：</span>{{ emlData.from }}</div>
              <div class="meta-item"><span class="meta-label">收件人：</span>{{ emlData.to }}</div>
              <div class="meta-item"><span class="meta-label">时&emsp;间：</span>{{ emlData.date }}</div>
            </div>
          </div>
          <div class="eml-body">
            <iframe v-if="emlData.isHtml"
                    :srcdoc="emlData.body"
                    class="eml-iframe"
                    @load="pvLoading = false"></iframe>
            <pre v-else
                 class="eml-plain">{{ emlData.body }}</pre>
          </div>
        </div>

        <!-- 4. 文本代码预览 -->
        <div v-else-if="pvType === 'text'"
             class="pv-text">
          <div class="text-toolbar">
            <span class="text-title"><i class="el-icon-document"></i> 文本内容</span>
            <el-button size="mini"
                       type="primary"
                       plain
                       icon="el-icon-document-copy"
                       @click="copyText">复制代码
            </el-button>
          </div>
          <div class="text-content">
            <pre><code>{{ textContent }}</code></pre>
          </div>
        </div>

        <!-- 5. 视频预览 -->
        <div v-else-if="pvType === 'video'"
             class="pv-media">
          <video :src="pvUrl"
                 controls
                 autoplay
                 class="media-video"
                 @loadeddata="pvLoading = false"></video>
        </div>

        <!-- 6. 音频预览 -->
        <div v-else-if="pvType === 'audio'"
             class="pv-media">
          <div class="audio-wrapper">
            <i class="el-icon-headset audio-icon"
               :class="{ 'is-playing': audioPlaying }"></i>
            <audio :src="pvUrl"
                   controls
                   autoplay
                   @play="audioPlaying = true"
                   @pause="audioPlaying = false"
                   @loadeddata="pvLoading = false"></audio>
          </div>
        </div>

        <!-- 7. 不支持格式 -->
        <div v-else
             class="pv-unsupported">
          <i class="el-icon-document-delete"></i>
          <h3>暂不支持该文件类型的在线预览</h3>
          <p>请下载后在本地应用程序中打开查看</p>
          <el-button type="primary"
                     icon="el-icon-download"
                     @click="downloadFile(pvFile)">下载文件
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import projectMixins from '@views/project/project.mixins'

// Chrome 支持的原生预览格式
const TYPE_MAP = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'],
  pdf: ['pdf'],
  html: ['html', 'htm'],
  eml: ['eml'],
  text: ['txt', 'json', 'xml', 'css', 'js', 'ts', 'md', 'log', 'yaml', 'yml'],
  video: ['mp4', 'webm', 'ogg'],
  audio: ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac']
}

export default {
  name: 'AttachmentViewer',
  mixins: [projectMixins],
  props: {
    files: {type: Array, default: () => []},
    urlField: {type: String, default: 'tmp_url'},
    getUrlMethod: {type: Function, default: null}
  },
  data () {
    return {
      pvVisible: false,
      pvLoading: false,
      pvType: '',
      pvFile: null,
      pvUrl: '',
      textContent: '',
      emlData: null,
      audioPlaying: false,
      // 图片平移缩放状态
      imgScale: 1,
      imgRotate: 0,
      imgX: 0,
      imgY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
      iconMap: {
        image: 'el-icon-picture',
        pdf: 'el-icon-document',
        html: 'el-icon-monitor',
        eml: 'el-icon-message',
        text: 'el-icon-tickets',
        video: 'el-icon-video-camera',
        audio: 'el-icon-headset',
        other: 'el-icon-files'
      },
      labelMap: {
        image: '图片',
        pdf: 'PDF',
        html: '网页',
        eml: '邮件',
        text: '文本',
        video: '视频',
        audio: '音频',
        other: '文件'
      }
    }
  },
  computed: {
    imgStyle () {
      return {
        transform: `translate(${this.imgX}px, ${this.imgY}px) scale(${this.imgScale}) rotate(${this.imgRotate}deg)`,
        transition: this.isDragging ? 'none' : 'transform 0.2s ease',
        cursor: this.isDragging ? 'grabbing' : 'grab'
      }
    },
    processedFiles () {
      return (this.files || []).map(item => {
        const name = this.getFileName(item)
        const type = this.getFileType(item)
        const isImg = type === 'image'
        const shortName = name.length > 12 ? name.substring(0, 10) + '...' : name
        const uploader = this.getUploaderName(item)
        const url = this.getFileUrl(item)
        const submitAt = item.submit_at || ''
        const tooltip = `文件名: ${name}\n上传人: ${uploader}\n上传时间: ${submitAt}`
        return {
          raw: item,
          name,
          type,
          isImg,
          shortName,
          uploader,
          url,
          tooltip
        }
      })
    },
    pvTitle () {
      if (!this.pvFile) return '附件预览'
      return this.pvFile.tooltip || this.getFileName(this.pvFile)
    }
  },
  methods: {
    // ---- 基础文件信息 ----
    getFileName (item) { return item.name || item.fileName || item.title || '未知文件名' },
    getUploaderName (item) {
      return item.submit_admin_name || item.creatorName || item.createName || item.uploader || item.creator || item.userName || ''
    },
    getFileExt (item) {
      const name = this.getFileName(item)
      const parts = name.split('.')
      return parts.length > 1 ? parts.pop().toLowerCase() : ''
    },
    getFileType (item) {
      if (item.type) {
        const t = item.type.toLowerCase()
        if (['pic', 'image', 'img'].includes(t)) return 'image'
        if (['pdf', 'html', 'eml', 'video', 'audio', 'text'].includes(t)) return t
      }
      const ext = this.getFileExt(item)
      for (const [type, exts] of Object.entries(TYPE_MAP)) {
        if (exts.includes(ext)) return type
      }
      return 'other'
    },
    getFileUrl (item) {
      if (this.getUrlMethod) return this.getUrlMethod(item)
      return item[this.urlField] || item.url || item.fileUrl || ''
    },
    handleImgError (e) {
      e.target.style.display = 'none'
    },

    // ---- 操作交互 ----
    downloadFile (item) {
      const rawItem = item.raw || item
      if (typeof this.downloadProgressAttachFile === 'function') {
        this.downloadProgressAttachFile(rawItem)
      } else {
        const url = item.url || this.getFileUrl(rawItem)
        if (url) {
          window.open(url, '_blank')
        } else {
          this.$message.error('无有效下载地址')
        }
      }
    },
    async previewFile (item) {
      this.pvFile = item
      const rawItem = item.raw || item
      this.pvType = item.type || this.getFileType(rawItem)
      this.pvLoading = true
      this.pvVisible = true
      this.resetImg()

      this.pvUrl = item.url || this.getFileUrl(rawItem) || ''
      if (!this.pvUrl) {
        this.$message.error('无法获取文件预览地址')
        this.pvLoading = false
        return
      }

      try {
        if (this.pvType === 'eml') {
          await this.loadEml(this.pvUrl)
        } else if (this.pvType === 'text') {
          await this.loadText(this.pvUrl)
        } else if (['pdf', 'html', 'image', 'video', 'audio'].includes(this.pvType)) {
          // 由对应的DOM元素触发 loaded 事件取消 loading，设置个兜底超时
          setTimeout(() => { this.pvLoading = false }, 5000)
        } else {
          this.pvLoading = false
        }
      } catch (e) {
        console.error('Preview error:', e)
        this.$message.error('文件加载失败')
        this.pvLoading = false
      }
    },
    closePreview () {
      this.pvVisible = false
      this.pvUrl = ''
      this.pvFile = null
      this.textContent = ''
      this.emlData = null
      this.audioPlaying = false
    },

    // ---- 图片查看器 ----
    dragStart (e) {
      this.isDragging = true
      this.startX = e.clientX - this.imgX
      this.startY = e.clientY - this.imgY
      const move = ev => {
        if (!this.isDragging) return
        this.imgX = ev.clientX - this.startX
        this.imgY = ev.clientY - this.startY
      }
      const up = () => {
        this.isDragging = false
        document.removeEventListener('mousemove', move)
        document.removeEventListener('mouseup', up)
      }
      document.addEventListener('mousemove', move)
      document.addEventListener('mouseup', up)
    },
    onWheel (e) {
      const delta = e.wheelDelta ? e.wheelDelta : -e.detail
      this.zoomImg(delta > 0 ? 0.1 : -0.1)
    },
    zoomImg (step) {
      this.imgScale = Math.min(Math.max(this.imgScale + step, 0.2), 5)
    },
    rotateImg () {
      this.imgRotate = (this.imgRotate + 90) % 360
    },
    resetImg () {
      this.imgScale = 1
      this.imgRotate = 0
      this.imgX = 0
      this.imgY = 0
    },

    // ---- 文本查看 ----
    async loadText (url) {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Fetch failed')
      this.textContent = await res.text()
      this.pvLoading = false
    },
    copyText () {
      navigator.clipboard.writeText(this.textContent).then(() => {
        this.$message.success('复制成功')
      }).catch(() => {
        const ta = document.createElement('textarea')
        ta.value = this.textContent
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$message.success('复制成功')
      })
    },

    // ---- 强健的 EML 解析 ----
    async loadEml (url) {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Fetch EML failed')
      const raw = await res.text()
      this.emlData = this.parseEmlRobust(raw)
      this.pvLoading = false
    },
    parseEmlRobust (raw) {
      const decodeStr = (str, charset) => {
        try {
          return new TextDecoder(charset || 'utf-8').decode(new Uint8Array([...str].map(c => c.charCodeAt(0))))
        } catch (e) { return str }
      }

      const parseHeaders = (text) => {
        const headers = {}
        const lines = text.split(/\r?\n/)
        let cur = ''
        for (const l of lines) {
          if (/^[ \t]/.test(l)) { if (cur) headers[cur] += ' ' + l.trim() } else {
            const m = l.match(/^([^:]+):\s*(.*)$/)
            if (m) {
              cur = m[1].toLowerCase()
              headers[cur] = m[2]
            }
          }
        }
        return headers
      }

      const decodeMime = (str) => {
        if (!str) return ''
        return str.replace(/=\?([^?]+)\?([BQbq])\?([^?]+)\?=/g, (_, charset, enc, content) => {
          try {
            return enc.toUpperCase() === 'B'
              ? decodeStr(atob(content), charset)
              : decodeStr(content.replace(/_/g, ' ').replace(/=([0-9A-F]{2})/gi, (__, h) => String.fromCharCode(parseInt(h, 16))), charset)
          } catch (e) { return content }
        })
      }

      // 分离头部和正文
      const parts = raw.split(/\r?\n\r?\n/)
      const headerText = parts.shift()
      const bodyText = parts.join('\n\n')
      const headers = parseHeaders(headerText)

      const hdr = k => decodeMime(headers[k] || '').replace(/^"|"$/g, '').trim()
      const info = {
        subject: hdr('subject') || '无主题',
        from: hdr('from') || '未知发件人',
        to: hdr('to') || '未知收件人',
        date: hdr('date') || '未知时间',
        body: '',
        isHtml: false
      }

      // 递归提取 multipart 内容
      const extractBody = (hdrs, bdy) => {
        const ct = hdrs['content-type'] || ''
        if (ct.toLowerCase().includes('multipart/')) {
          const m = ct.match(/boundary="?([^";\s]+)"?/i)
          if (m) {
            const boundary = m[1]
            const subParts = bdy.split(new RegExp(`--${boundary}(?:--)?\r?\n?`))
            let hContent = ''
            let pContent = ''
            for (const sp of subParts) {
              if (!sp.trim()) continue
              const spArr = sp.split(/\r?\n\r?\n/)
              const shText = spArr.shift()
              const sbText = spArr.join('\n\n')
              const sh = parseHeaders(shText)
              const res = extractBody(sh, sbText)
              if (res.isHtml) {
                hContent = hContent || res.body
              } else {
                pContent = pContent || res.body
              }
            }
            if (hContent) return {body: hContent, isHtml: true}
            return {body: pContent, isHtml: false}
          }
        }

        const enc = (hdrs['content-transfer-encoding'] || '').toLowerCase()
        const charsetMatch = ct.match(/charset="?([^";\s]+)"?/i)
        const charset = charsetMatch ? charsetMatch[1] : 'utf-8'

        let decoded = bdy
        try {
          if (enc.includes('base64')) {
            decoded = decodeStr(atob(bdy.replace(/\s/g, '')), charset)
          } else if (enc.includes('quoted-printable')) {
            const qp = bdy.replace(/=\r?\n/g, '').replace(/=([0-9A-F]{2})/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
            decoded = decodeStr(qp, charset)
          }
        } catch (e) { console.error('Decode part error', e) }

        return {
          body: decoded,
          isHtml: ct.toLowerCase().includes('text/html')
        }
      }

      const extracted = extractBody(headers, bodyText)
      info.body = extracted.body || '无正文内容'
      info.isHtml = extracted.isHtml
      return info
    },
  }
}
</script>

<style lang="scss"
       scoped>
.attachment-viewer {
  width: 100%;
}

.viewer-header {
  margin-bottom: 8px;
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
}

.files-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  max-height: 400px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 4px 12px 4px;
  /* 自定义滚动条 */
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
}

/* 紧凑详情卡片 - 显示按钮与上传人，适应网格布局 */
.file-card {
  width: 86px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
  padding: 4px 4px 2px;

  &:hover {
    background: #fff;
    border-color: #e2e8f0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

    .card-name {
      color: #3b82f6;
    }
  }
}

.card-icon {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  position: relative;
  background: #fff;
  border: 1px solid #e2e8f0;
  cursor: pointer;

  .img-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }

  .file-badge {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 8px;
    transform: scale(0.8);
    background: #334155;
    color: #fff;
    padding: 1px 4px;
    border-radius: 3px;
    font-weight: bold;
    border: 1px solid #fff;
  }
}

/* 根据文件类型定制颜色 */
$types: (
  pdf: (#ef4444, #fef2f2, #fca5a5),
  html: (#0ea5e9, #f0f9ff, #7dd3fc),
  eml: (#8b5cf6, #f5f3ff, #c4b5fd),
  text: (#10b981, #ecfdf5, #6ee7b7),
  video: (#f59e0b, #fffbeb, #fcd34d),
  audio: (#ec4899, #fdf2f8, #f9a8d4)
);
@each $name, $colors in $types {
  .card-icon.#{$name} {
    color: nth($colors, 1);
    background: nth($colors, 2);
    border-color: nth($colors, 3);

    .file-badge {
      background: nth($colors, 1);
    }
  }
}

.card-icon.image {
  border-color: #cbd5e1;
}

.card-name {
  margin-top: 4px;
  font-size: 11px;
  color: #334155;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  cursor: pointer;
  transition: color 0.2s;
}

.card-uploader {
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;
}

.card-actions-inline {
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  padding-top: 2px;

  .action-text-btn {
    font-size: 10px;
    color: #64748b;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 2px;
    transition: color 0.2s;

    &:hover {
      color: #3b82f6;
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: #94a3b8;

  i {
    font-size: 32px;
    margin-bottom: 8px;
  }

  p {
    font-size: 12px;
    margin: 0;
  }
}

</style>

<style lang="scss">
/* 预览层全局统一样式 (必须脱离 scoped，否则无法作用于 append-to-body 的 el-dialog) */
.preview-dialog {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  display: flex !important;
  flex-direction: column;
  margin-top: 1vh !important;
  margin-bottom: 0 !important;
  height: 97vh !important; /* 严格固定弹窗总高度 */

  .el-dialog__header {
    padding: 12px 16px;
    border-bottom: 1px solid #334155;
    background: #0f172a;
    flex-shrink: 0;

    .el-dialog__title {
      color: #f8fafc;
      font-size: 15px;
      font-weight: 600;
    }

    .el-dialog__headerbtn {
      top: 12px;
      right: 16px;

      .el-dialog__close {
        color: #94a3b8;
        font-size: 18px;
      }
    }
  }

  .el-dialog__body {
    padding: 0 !important;
    flex: 1 !important;
    background: #020617;
    position: relative !important; /* 绝对定位的锚点 */
    overflow: hidden !important; /* 绝对禁止内部撑开 */
  }
}
</style>

<style lang="scss"
       scoped>

.preview-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* 脱离文档流，利用 absolute 严格限制在 el-dialog__body 内部，绝不撑大父级 */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  & > div:not(.pv-unsupported), .pv-iframe {
    width: 100%;
    height: 100%;
  }
}

/* 图片预览 */
.pv-image {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    user-select: none;
    -webkit-user-drag: none;
  }

  .img-toolbar {
    position: absolute;
    bottom: 24px;
    background: rgba(30, 41, 59, 0.8);
    backdrop-filter: blur(8px);
    padding: 6px 12px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);

    .zoom-text {
      color: #e2e8f0;
      font-size: 13px;
      font-weight: bold;
      width: 40px;
      text-align: center;
    }

    ::v-deep .el-button {
      background: rgba(255, 255, 255, 0.1);
      border: none;
      color: #f8fafc;
      padding: 6px 10px;

      &:hover {
        background: #3b82f6;
      }
    }
  }
}

/* iframe 预览 (PDF/HTML) */
.pv-iframe {
  border: none;
  background: #fff;
}

/* 文本预览 */
.pv-text {
  display: flex;
  flex-direction: column;
  background: #1e293b;

  .text-toolbar {
    padding: 10px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #0f172a;
    border-bottom: 1px solid #334155;

    .text-title {
      color: #f8fafc;
      font-size: 13px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }

  .text-content {
    flex: 1;
    overflow: auto;
    padding: 16px;

    pre {
      margin: 0;
      color: #e2e8f0;
      font-family: 'Fira Code', Consolas, monospace;
      font-size: 13px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-wrap: break-word;
    }
  }
}

/* EML预览 */
.pv-eml {
  display: flex;
  flex-direction: column;
  background: #f8fafc;

  .eml-header {
    padding: 16px 24px;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;

    .eml-subject {
      margin: 0 0 12px 0;
      font-size: 18px;
      color: #0f172a;
    }

    .eml-meta {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .meta-item {
        font-size: 13px;
        color: #334155;
        display: flex;
      }

      .meta-label {
        color: #64748b;
        width: 60px;
        flex-shrink: 0;
      }
    }
  }

  .eml-body {
    flex: 1;
    overflow: hidden;
    position: relative;

    .eml-iframe {
      width: 100%;
      height: 100%;
      border: none;
      background: #fff;
    }

    .eml-plain {
      margin: 0;
      padding: 24px;
      font-size: 14px;
      line-height: 1.6;
      color: #1e293b;
      white-space: pre-wrap;
      word-wrap: break-word;
      height: 100%;
      overflow-y: auto;
      background: #fff;
      font-family: inherit;
    }
  }
}

/* 媒体预览 */
.pv-media {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  .media-video {
    max-width: 100%;
    max-height: 100%;
    border-radius: 8px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
    outline: none;
  }

  .audio-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    background: #1e293b;
    padding: 40px 60px;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3);

    .audio-icon {
      font-size: 64px;
      color: #3b82f6;
      transition: all 0.3s;

      &.is-playing {
        animation: pulse 2s infinite;
        color: #60a5fa;
      }
    }
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* 不支持格式 */
.pv-unsupported {
  text-align: center;
  color: #94a3b8;

  i {
    font-size: 48px;
    color: #475569;
    margin-bottom: 16px;
  }

  h3 {
    color: #e2e8f0;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 24px;
    font-size: 13px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/project/node-views/AttachmentViewer.vue